import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LoginScreen } from './components/LoginScreen.js';
import { ShortcutHelp } from './components/ShortcutHelp.js';
import { UnifiedDashboard } from './components/UnifiedDashboard.js';
import { type NavView, viewTitle } from './lib/destinations.js';
import { buildShortcuts, useKeyboardShortcuts } from './lib/keyboard.js';
import { useRealtimeEvents } from './lib/realtime.js';
import { useSession } from './lib/session.js';
import { adjacentThreadId, flattenCachedThreadIds, type ThreadCache } from './lib/thread-nav.js';
import { useUrlParam } from './lib/url-state.js';

export interface AppProps {
  queryClient?: QueryClient;
}

/**
 * batch-166 — "fully dynamic" React Query defaults. The dashboard already polls
 * the hot surfaces (see LIVE_POLL_MS) + invalidates on every mutation; these
 * defaults make it feel LIVE without a manual reload:
 *   - refetchOnWindowFocus/Reconnect TRUE → returning to the tab (or a network
 *     reconnect) refreshes instantly instead of waiting for the next poll. Safe:
 *     editable forms use local-state overrides (`value = local ?? server`) or the
 *     ReviewWorkspace dirty-guard, so a background refetch never clobbers typing.
 *   - staleTime 30s → a focus/mount within 30s of the last fetch is treated as
 *     fresh (no refetch storm on rapid alt-tabs / reconnects). SSE pushes the
 *     real-time updates anyway, so a focus-refetch on fresh data is pure churn —
 *     raising this from 10s→30s (matching LIVE_POLL_MS) was the biggest single
 *     cut to the Inbox "flicker on refresh" (Inbox-rebuild Phase 0).
 */
export const queryClientDefaults = {
  queries: {
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: (failureCount: number, err: unknown) => {
      // No retries on auth failures — surface immediately.
      const status = (err as { status?: number })?.status;
      if (status === 401 || status === 403 || status === 404) return false;
      return failureCount < 2;
    },
  },
} as const;

export function App({ queryClient }: AppProps = {}) {
  const client = useMemo(
    () => queryClient ?? new QueryClient({ defaultOptions: queryClientDefaults }),
    [queryClient],
  );

  return (
    <QueryClientProvider client={client}>
      <AuthGate />
    </QueryClientProvider>
  );
}

/**
 * Gate the dashboard on session state.
 *
 *   - bearer mode       : state='disabled' → mount, no /auth/me call.
 *   - cookie mode       : state='unauthenticated' on 401 → LoginScreen.
 *   - hybrid + bearer   : state='bearer_fallback' on 401 → mount, API
 *                         calls succeed via Authorization: Bearer …
 *   - hybrid, no bearer : state='unauthenticated' → LoginScreen
 *                         (same as cookie mode).
 *   - 'loading'         : tiny placeholder while /auth/me is in flight.
 */
function AuthGate() {
  const { state, refresh } = useSession();

  if (state.status === 'loading') {
    return (
      <div
        data-testid="session-loading"
        className="flex h-screen w-screen items-center justify-center bg-zinc-950"
      >
        <span
          className="inline-block h-1.5 w-1.5 animate-pulse rounded-full"
          style={{ backgroundColor: 'var(--accent)' }}
        />
      </div>
    );
  }
  if (state.status === 'unauthenticated') {
    return <LoginScreen onSuccess={() => void refresh()} />;
  }
  // 'disabled' (bearer-only), 'authenticated', and 'bearer_fallback'
  // (hybrid with bearer configured) all mount the app.
  return <AppInner />;
}

/**
 * The top-level views. URL persistence uses `?view=…`; B2 made the "Today" home the
 * default surface (`null`/empty → unified-today). R26 RETIRED Classic: every legacy
 * value (`inbox`/`threads`/`classic`/`followups`/`contacts`/`candidates`/`settings`)
 * now REMAPS to its Unified equivalent (decision #4, S5) so old bookmarks keep
 * working; `parseView` only ever returns a `unified-*` view.
 */
export function parseView(raw: string | null): NavView {
  switch (raw) {
    // B2 — the "Today" home is the default landing (empty/unknown → Today).
    case 'today':
    case 'unified-today':
      return 'unified-today';
    // legacy Classic aliases → the Unified inbox.
    case 'classic':
    case 'threads':
    case 'inbox':
    case 'unified':
      return 'unified';
    case 'followups':
    case 'unified-followups':
      return 'unified-followups';
    case 'engagement':
    case 'unified-engagement':
      return 'unified-engagement';
    case 'connection-requests':
    case 'unified-connection-requests':
      return 'unified-connection-requests';
    case 'job-changes':
    case 'unified-job-changes':
      return 'unified-job-changes';
    case 'keep-in-touch':
    case 'unified-keep-in-touch':
      return 'unified-keep-in-touch';
    case 'connect':
    case 'unified-connect':
      return 'unified-connect';
    case 'contacts':
    case 'unified-contacts':
      return 'unified-contacts';
    case 'candidates':
    case 'unified-candidates':
      return 'unified-candidates';
    case 'automation':
    case 'unified-automation':
      return 'unified-automation';
    case 'settings':
    case 'unified-settings':
      return 'unified-settings';
    default:
      return 'unified-today';
  }
}

function AppInner() {
  // batch-176 — real-time push: subscribe to the SSE stream (authed only) and
  // invalidate queries on each change, so the UI updates instantly (polling is
  // now just the fallback).
  useRealtimeEvents(useQueryClient(), true);
  const [selectedThreadId, setSelectedThreadId] = useUrlParam('thread');
  const [selectedContactId, setSelectedContactId] = useUrlParam('contact');
  const [viewParam, setViewParam] = useUrlParam('view');
  const view: NavView = parseView(viewParam);

  useEffect(() => {
    document.title = `${viewTitle(view)} · Comms`;
  }, [view]);
  const [helpOpen, setHelpOpen] = useState(false);
  const queryClient = useQueryClient();

  const setView = (next: NavView) => {
    // B2 — "Today" is the implicit default/home — drop the param so the home URL
    // stays clean (the inbox and every other view carry an explicit `?view`).
    setViewParam(next === 'unified-today' ? null : next);
  };

  // Open a thread / contact in the Unified workspace (the single source of truth
  // is the URL, so back/forward works).
  const goToUnifiedThread = (threadId: string) => {
    setSelectedThreadId(threadId);
    setView('unified');
  };

  const goToUnifiedContact = (contactId: string) => {
    setSelectedContactId(contactId);
    setView('unified-contacts');
  };

  /**
   * Auto-advance after defer / no-response-needed.
   *
   * Reads the cached infinite-query data for the threads list and
   * picks the row immediately after the just-triaged one. The lookup
   * happens BEFORE the cache is invalidated, so the deferred row is
   * still in the snapshot — we use that snapshot to compute "next".
   */
  // R15 review — useCallback so the TriageProvider's context value stays stable
  // (it memoizes over onTriaged), avoiding a re-render of every visible row's
  // TriageActions on each poll/SSE-driven App render.
  const handleTriaged = useCallback(
    (currentThreadId: string) => {
      // batch-152 (CODEX #9) — auto-advance must traverse the ACTIVE view's cache.
      // Follow-up views render from ['followups'] (shape {items}); the inbox from
      // ['threads'] (shape {pages:[{items}]}). Keying only off ['threads'] cleared
      // the selection in follow-up views instead of moving to the next item.
      const key = view.includes('followups') ? 'followups' : 'threads';
      const queries = queryClient.getQueriesData<ThreadCache>({ queryKey: [key] });
      for (const [, data] of queries) {
        const ids = flattenCachedThreadIds(data);
        if (ids.includes(currentThreadId)) {
          // +1 from the just-triaged row is "next"; null at the end → deselect.
          setSelectedThreadId(adjacentThreadId(ids, currentThreadId, 1));
          return;
        }
      }
      setSelectedThreadId(null);
    },
    [view, queryClient, setSelectedThreadId],
  );

  /**
   * Pick the next / previous thread id from the cached infinite-query
   * data. Mirrors handleTriaged's traversal logic, but parameterized
   * over direction. Works across both the inbox queue (queryKey
   * ['threads', …]) and the follow-ups queue (['followups', …]) so the
   * J/K shortcuts make sense in either view.
   */
  const pickAdjacentThread = (delta: 1 | -1): string | null => {
    const cacheKeyByView: Record<NavView, string | null> = {
      'unified-today': null,
      unified: 'threads',
      'unified-followups': 'followups',
      'unified-engagement': null,
      'unified-connection-requests': null,
      'unified-job-changes': null,
      'unified-keep-in-touch': null,
      'unified-connect': null,
      'unified-contacts': null,
      'unified-candidates': null,
      'unified-automation': null,
      'unified-settings': null,
    };
    const expectedKey = cacheKeyByView[view];
    if (expectedKey === null) return null;
    const queries = queryClient.getQueriesData<ThreadCache>({ queryKey: [expectedKey] });
    for (const [, data] of queries) {
      const ids = flattenCachedThreadIds(data);
      if (ids.length === 0) continue;
      // No selection yet: J picks the first row, K picks the last.
      if (selectedThreadId === null) return adjacentThreadId(ids, null, delta);
      if (!ids.includes(selectedThreadId)) continue;
      return adjacentThreadId(ids, selectedThreadId, delta);
    }
    return null;
  };

  // R12 — the ONE keyboard registry: it both binds (useKeyboardShortcuts) and
  // renders the `?` overlay (ShortcutHelp reads `shortcuts`), so a dead/advertised
  // shortcut can't exist. g-keys navigate to the (routed) view destinations.
  const shortcuts = buildShortcuts({
    selectNext: () => {
      const id = pickAdjacentThread(1);
      if (id !== null) setSelectedThreadId(id);
    },
    selectPrev: () => {
      const id = pickAdjacentThread(-1);
      if (id !== null) setSelectedThreadId(id);
    },
    focusDraft: () => {
      // The draft editor lives inside ReviewWorkspace; only mounted when a thread
      // is selected → a no-op otherwise (keyboard nav stays predictable).
      document.getElementById('draft-editor')?.focus();
    },
    focusSearch: () => {
      // R8 — the search lives in the global topbar (`global-search`).
      document.querySelector<HTMLElement>('[data-testid="global-search"]')?.focus();
    },
    analyze: () => {
      // Click the existing Analyze button rather than calling the mutation — reuses
      // the panel's pending/feedback/invalidation + the single Anthropic call site.
      document.querySelector<HTMLButtonElement>('[data-testid="intelligence-analyze"]')?.click();
    },
    toggleHelp: () => setHelpOpen((v) => !v),
    // A2 — drive the OPEN thread's canvas triage buttons (inbox / follow-ups only
    // mount one canvas at a time), so the undo toast + auto-advance + snooze picker
    // all come from the existing buttons. No-op on views without a canvas triage.
    triageDone: () =>
      document
        .querySelector<HTMLButtonElement>('.triage-canvas [data-testid="triage-done"]')
        ?.click(),
    triageSnooze: () =>
      document
        .querySelector<HTMLButtonElement>('.triage-canvas [data-testid="triage-snooze"]')
        ?.click(),
    triageMute: () =>
      document
        .querySelector<HTMLButtonElement>('.triage-canvas [data-testid="triage-mute"]')
        ?.click(),
    goInbox: () => setView('unified'),
    goFollowups: () => setView('unified-followups'),
    goEngagement: () => setView('unified-engagement'),
    goJobChanges: () => setView('unified-job-changes'),
    goConnectionRequests: () => setView('unified-connection-requests'),
    goKeepInTouch: () => setView('unified-keep-in-touch'),
    goContacts: () => setView('unified-contacts'),
    goIdentityMatches: () => setView('unified-candidates'),
    goSetup: () => setView('unified-settings'),
  });
  // R12 review — suspend the bindings while the help overlay is open, so g-keys /
  // j / k don't fire UNDER the modal (the focus-trapped panel isn't an editable
  // target). `?` opens it; Esc / the close button / the scrim close it.
  useKeyboardShortcuts(shortcuts, { enabled: !helpOpen });

  return (
    <>
      <UnifiedDashboard
        currentView={view}
        selectedThreadId={selectedThreadId}
        selectedContactId={selectedContactId}
        onSelectThread={setSelectedThreadId}
        onSelectContactId={setSelectedContactId}
        onOpenThread={goToUnifiedThread}
        onTriaged={handleTriaged}
        onChangeView={setView}
        onSelectContact={goToUnifiedContact}
        onOpenShortcuts={() => setHelpOpen(true)}
      />
      <ShortcutHelp open={helpOpen} onClose={() => setHelpOpen(false)} shortcuts={shortcuts} />
    </>
  );
}
